import {
	isRouteErrorResponse,
	Link,
	useLoaderData,
	useRouteError,
} from '@remix-run/react'
import {
	type LoaderFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from '@remix-run/node'
import Section from '#app/components/layout/section.tsx'
import Button from '#app/components/buttons/button'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { TypographyParagraph } from '../components/typography'
import generateCollectionMeta from '#app/utils/meta/collectionMeta.ts'
import { mapNFTsToCollection } from '#app/utils/misc.server.ts'
import MetadataProfile from '#app/components/metadataProfile/collectionLoreMetadataProfile.tsx'
import {
	getNFTsForCollection,
	getAttestationv2,
} from '#app/server/db.server.ts'
import { mapReferenceEntriesToFormData } from '#app/utils/misc.tsx'
import { AttestationType, CollectionNFTEntry } from '#types/loreTypes.tsx'

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
	matches,
}) => {
	if (data) {
		const { origin, collectionAttestation } = data
		const { pathname } = location
		const metaPayload = generateCollectionMeta({
			origin,
			path: pathname,
			collectionAttestation,
		})
		return metaPayload
	} else {
		const parentMeta = matches.flatMap(match => match.meta ?? [])
		return parentMeta
	}
}

export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	let collectionId = params.collectionCid
	let attestationCid = params.attestationCid

	if (!collectionId || !attestationCid) {
		return json({
			collection: undefined,
			origin: url.origin,
			atomicLoreUrl: url.origin,
			atomicSignUrl: import.meta.env.VITE_ATOMICSIGN_URL,
		})
	}

	let collectionAttestation: AttestationType | undefined = undefined
	let collectionLore: AttestationType | undefined = undefined
	let nfts: CollectionNFTEntry[] = []
	let nftMessage: string = ''
	let nftCount = 0

	try {
		const retrievedcollection = await getAttestationv2({
			attestationIPFSCID: collectionId,
		})
		if (
			retrievedcollection &&
			retrievedcollection.attestations &&
			retrievedcollection.attestations.length
		) {
			collectionAttestation = retrievedcollection.attestations[0]
		} else {
			console.error('COLLECTION LORE ATTESTATION NOT FOUND', {
				cid: params.collectionCid,
			})
			throw new Response(
				`Error retrieving attestation for collection with cid or slug: ${params.collectionCid}`,
				{
					status: 500,
				},
			)
		}
	} catch (e) {
		console.error('ERROR LOADING ATTESTATION FOR COLLECTION', {
			e,
			cid: params.collectionCid,
		})
		throw new Response(
			`Error retrieving attestation for collection with cid or slug: ${params.cid}`,
			{
				status: 500,
			},
		)
	}

	try {
		const retrievedCollectionLore = await getAttestationv2({
			attestationIPFSCID: attestationCid,
		})
		if (
			retrievedCollectionLore &&
			retrievedCollectionLore.attestations &&
			retrievedCollectionLore.attestations.length
		) {
			collectionLore = retrievedCollectionLore.attestations[0]
		} else {
			console.error('ERROR LOADING ATTESTATION FOR COLLECTION', {
				cid: params.attestationCid,
			})
			throw new Response('Error retrieving attestations for collection!', {
				status: 500,
			})
		}
	} catch (e) {
		console.error('ERROR LOADING ATTESTATION FOR COLLECTION', {
			e,
			cid: params.attestationCid,
		})
		throw new Response('Error retrieving attestations for collection!', {
			status: 500,
		})
	}

	try {
		const nftrr = await getNFTsForCollection({
			attestationCID: collectionId,
		})
		if (nftrr) {
			if (
				nftrr.nfts &&
				nftrr.nfts?.length &&
				nftrr?.nfts.length > 0 &&
				Array.isArray(nftrr.nfts)
			) {
				nfts = nftrr.nfts
			} else {
				nfts = []
			}
			nftMessage = nftrr.message
		}
	} catch (e) {
		console.error('ERROR FETCHING NFTS', e)
		nfts = []
	}

	try {
		if (collectionAttestation) {
			// @ts-ignore
			const pendingSignNFTProperties =
				collectionAttestation.references?.entry?.nft &&
				collectionAttestation.template.slug === 'collection'
					? collectionAttestation.references.entry.nft
						? mapReferenceEntriesToFormData(
								collectionAttestation.references.entry.nft,
							)
						: []
					: []
			if (pendingSignNFTProperties && pendingSignNFTProperties.length > 0) {
				collectionAttestation.properties.nfts = pendingSignNFTProperties
				const mappedNFTResponse = await mapNFTsToCollection(
					collectionAttestation,
				)
				if (mappedNFTResponse.nftCount) nftCount = mappedNFTResponse.nftCount
			}
		}
	} catch (e) {
		console.error('ERROR UPDATING PENDING SIGN NFT PROPERTIES', e)
	}

	return json({
		cid: params.attestationCid,
		collectionAttestation,
		collectionLore,
		nfts,
		nftMessage,
		nftCount: nftCount > nfts.length ? nftCount : nfts.length,
		atomicLoreUrl: url.origin,
		origin: url.origin,
	})
}

export default function metadataPage() {
	const {
		cid,
		collectionAttestation,
		collectionLore,
		nfts,
		nftMessage,
		nftCount,
		atomicLoreUrl,
		origin,
	} = useLoaderData<typeof loader>()

	return (
		<MetadataProfile
			cid={cid}
			collection={collectionAttestation}
			collectionLore={collectionLore}
			nfts={nfts}
			nftMessage={nftMessage}
			nftCount={nftCount}
			atomicLoreUrl={atomicLoreUrl}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	}
}
